// src/utils/categoryHelpers.ts

import { useTranslation } from 'react-i18next';
import { Category } from './supabaseClient';
import { ArTranslation } from '../types/custom';

/**
 * Get the localized name for a category based on current language
 * @param category - The category object
 * @param language - Current language ('en' or 'ar')
 * @returns Localized category name
 */
export const getCategoryName = (category: Category | null, language: string): string => {
	if (!category) return '';
	const arTranslation = category.ar as ArTranslation | null;
	if (language === 'ar' && arTranslation?.name) {
		return arTranslation.name;
	}
	return category.name || '';
};

/**
 * Get the localized description for a category based on current language
 * @param category - The category object
 * @param language - Current language ('en' or 'ar')
 * @returns Localized category description
 */
export const getCategoryDescription = (category: Category | null, language: string): string | null => {
	if (!category) return null;
	const arTranslation = category.ar as ArTranslation | null;
	if (language === 'ar' && arTranslation?.description) {
		return arTranslation.description;
	}
	return category.description;
};

/**
 * Hook to get localized category data
 * @param category - The category object
 * @returns Object with localized name and description
 */
export const useLocalizedCategory = (category: Category) => {
	const { i18n } = useTranslation();

	return {
		name: getCategoryName(category, i18n.language),
		description: getCategoryDescription(category, i18n.language)
	};
};